import { Theme } from 'theme-ui'
import arrow from '../images/small-arrow.svg'
import arrowGreen from '../images/small-arrow-green.svg'
import arrowhead from '../images/small-arrowhead.svg'
import { modularScale, fluidTypography } from '../utils/mixins'

const formInput = {
  color: '#151515',
  border: '1px solid #d4d7d7',
  backgroundColor: '#e7eaea',
  borderRadius: '0',
  height: '4.75em',
  transition: 'background-color .75s ease-in-out',
  padding: '1.5884955747rem 1rem',
  display: 'block',
  width: '100%',
  fontWeight: '400',
  lineHeight: '1.5',
  backgroundClip: 'padding-box',
  overflow: 'visible',
  marginBottom: '1rem',
  ':focus': {
    outline: '0',
    boxShadow: '0 0 0 .2rem rgba(0,123,255,.25)',
  },
}

export const theme: Theme = {
  // breakpoints: mobile, tablet, laptop, desktop
  breakpoints: ['640px', '1024px', '1366px', '1680px'],
  space: modularScale(0.125, 1.26, 'rem'),
  fontSizes: fluidTypography(1, [1.21, 1.26], 0.28, 'rem'),
  fonts: {
    body: '"Open Sans", sans-serif',
    heading: '"Gottak", serif',
    monospace: 'Menlo, monospace',
  },
  fontWeights: {
    light: 300,
    body: 400,
    medium: 500,
    heading: 700,
    bold: 700,
    black: 800,
  },
  lineHeights: {
    body: 1.6,
    heading: 1.1,
  },
  colors: {
    text: '#151515',
    background: '#fff',
    primary: '#a3ed25',
    contrast: '#4F810A',
    secondary: '#67AA06',
    accent: '#edfec9',
    muted: '#e7eaea',
    darkMuted: '#f3f5f5',
    disabled: '#888',
    lightDisabled: '#bdbdbd',
    error: '#f63',
    gridBlue: '#012169',
    // wizard forms (src/forms/wizard): secondary copy such as slider bounds
    subtle: '#707070',
    // the EcoHub quote panel (src/forms/wizard/result/ecohub-quote.tsx)
    ink: '#0d1b2a',
  },
  borders: {
    single: '0.0625rem solid #151515',
    double: '0.125rem solid #151515',
    active: '0.25rem solid #9ad43a',
    muted: '0.0625rem solid #e7eaea',
    primaryBg: '0.0625rem solid #7ca933',
    activeBg: '0.0625rem solid #9AD43A',
    disabledBg: '0.0625rem solid #828282',
    mutedBg: '0.0625rem solid #d4d7d7',
  },
  borderWidths: {
    single: '0.0625rem',
    double: '0.125rem',
    divider: '1.5rem',
  },
  shadows: {
    card: '0 0 1rem 0.3rem rgba(0,0,0,0.1)',
  },
  badges: {
    new: {
      color: 'background',
      bg: 'contrast',
      padding: '0.125rem 0.25rem',
    },
  },
  styles: {
    root: {
      fontFamily: 'body',
      lineHeight: 'body',
      color: 'text',
    },
    a: {
      color: 'text',
      fontWeight: 'bold',
    },
  },
  layout: {
    container: {
      maxWidth: '76%',
    },
    newContainer: {
      maxWidth: '1340px',
      paddingLeft: ['20px','25px','30px'],
      paddingRight: ['20px','25px','30px'],
    },
    wide: {
      maxWidth: '92%',
    },
    newWide: {
      maxWidth: '1610px',
      paddingLeft: ['20px','25px','30px'],
      paddingRight: ['20px','25px','30px'],
    },
    banner: {
      maxWidth: '92%',
    },
    full: {
      maxWidth: 'none',
    },
  },
  grids: {
    default: {
      columnGap: 11,
      rowGap: 14,
      gridTemplateColumns: 'repeat(12, 1fr)',
    },
    equal: {
      gap: 11,
      gridTemplateColumns: 'repeat(12, 1fr)',
    },
  },
  text: {
    default: {
      fontSize: 0,
      fontFamily: 'body',
      fontWeight: 'body',
      lineHeight: 'body',
    },
    subHeader:{
      fontSize: ['18px','24px','30px'],
      fontFamily: 'body',
      fontWeight: 'light',
      lineHeight: 1.3,
    },
    intro: {
      fontSize: 1,
      fontFamily: 'body',
      fontWeight: 'light',
      lineHeight: 'body',
    },
    head: {
      fontSize: 2,
      fontFamily: 'body',
      fontWeight: 'light',
      lineHeight: 'body',
    },
    hero: {
      fontSize: 2,
      fontFamily: 'body',
      fontWeight: 'body',
      lineHeight: 'body',
    },
    primary: {
      color: 'primary',
      textTransform: 'uppercase',
      fontWeight: 'bold',
      lineHeight: 'heading',
    },
    contrast: {
      color: 'contrast',
      textTransform: 'uppercase',
      fontWeight: 'bold',
      lineHeight: 'heading',
    },
    label: {
      fontWeight: 'bold',
    },
    errorLabel: {
      marginTop: '8px',
      fontSize: 'min(max(75%,.83vw),112.5%)',
      minHeight: '1rem',
      color: 'error',
    },
    caption: {
      fontSize: '0.94rem',
    },
    newHeading: {
      fontSize: ['38px', '42px', '46px', '50px' ,'60px'],
      fontFamily: 'heading',
      fontWeight: 'heading',
      lineHeight: 'heading',
    },
    heading: {
      fontFamily: 'heading',
      fontWeight: 'heading',
      lineHeight: 'heading',
    },
    card: {
      fontSize: ['22px','24px','26px'],
      fontFamily: 'body',
      fontWeight: 'heading',
      lineHeight: 1.1,
      wordBreak: 'break-word',
    },
    cardText: {
      fontSize: ['14px',null,'16px'],
      fontFamily: 'body',
      fontWeight: 'body',
      lineHeight: 1.7,
    },
    xl: {
      fontSize: 7,
      fontFamily: 'heading',
      fontWeight: 'heading',
      lineHeight: 'heading',
    },
    lrg: {
      fontSize: 5,
      fontFamily: 'heading',
      fontWeight: 'heading',
      lineHeight: 'heading',
    },
    newMed: {
      fontSize: ['24px', null, null, '26px', '28px'],
      fontFamily: 'heading',
      fontWeight: 'heading',
      lineHeight: 1.2,
    },
    med: {
      fontSize: 3,
      fontFamily: 'heading',
      fontWeight: 'heading',
      lineHeight: 1.2,
    },
    newSml: {
      fontSize: ['20px', null, null, '22px', '24px'],
      fontFamily: 'body',
      fontWeight: 'heading',
      lineHeight: 1.2,
    },
    sml: {
      fontSize: 2,
      fontFamily: 'heading',
      fontWeight: 'heading',
      lineHeight: 1.3,
    },
    newXs: {
      fontSize: ['14px', null , null, '16px', '18px'],
      fontFamily: 'body',
      fontWeight: 'body',
      lineHeight: 1.3,
    },
    xs: {
      fontSize: 1,
      fontFamily: 'heading',
      fontWeight: 'heading',
      lineHeight: 1.3,
    },
    xxs: {
      fontSize: ['12px', null , null, '14px', '16px'],
      fontFamily: 'body',
      fontWeight: 'body',
      lineHeight: 1.3,
    },
  },
  links: {
    nav: {
      display: 'block',
      color: 'inherit',
      fontSize: '14px',
      textDecoration: 'none',
      fontWeight: 'body',
      transition: 'color 0.2s linear, text-decoration 0.2s linear',
      '&.active': {
        fontWeight: 'bold',
      },
      '&:hover': {
        color: 'contrast',
        textDecoration: 'underline',
      },
    },
    tab: {
      overflow: 'hidden',
      display: 'flex',
      position: 'relative',
      fontSize: '0.94rem',
      color: 'inherit',
      backgroundColor: 'muted',
      textDecoration: 'none',
      alignItems: 'center',
      justifyContent: 'center',
      textAlign: 'center',
      border: 'mutedBg',
      '&.active': {
        fontSize: '1rem',
        fontWeight: 'bold',
        border: 'none',
        backgroundColor: 'background',
      },
      '&.active::after': {
        content: '""',
        display: 'block',
        position: 'absolute',
        width: '65%',
        margin: 'auto 0',
        bottom: 0,
        borderBottom: 'active',
      },
      '&:hover': {
        backgroundColor: 'background',
        color: 'contrast',
      },
    },
    button: {
      display: 'inline-block',
      color: 'text',
      textAlign: 'center',
      textDecoration: 'none',
      border: 'solid 1px',
      borderColor: 'primary',
      backgroundColor: 'primary',
      fontFamily: 'body',
      fontWeight: 'bold',
      borderRadius: '10rem',
      minWidth: '15em',
      padding: '21px',
      lineHeight: 1,
      fontSize: '16px',
      cursor: 'pointer',
      transition: 'background-color 0.2s linear, color 0.2s linear, border-color 0.2s linear',
      
      '&:hover': {
        color: 'background',
        backgroundColor: 'text',
        borderColor: 'primary',
      },
    },
    buttonDisabled: {
      display: 'inline-block',
      color: 'text',
      textAlign: 'center',
      textDecoration: 'none',
      backgroundColor: 'lightDisabled',
      fontFamily: 'body',
      fontWeight: 'bold',
      border: 'disabledBg',
      borderRadius: '10rem',
      minWidth: '10em',
      paddingY: 11,
      paddingX: 13,
      '&:hover': {
        color: 'background',
        backgroundColor: 'lightDisabled',
        borderColor: 'disabledBg',
      },
    },
    buttonSecondary: {
      display: 'inline-block',
      color: 'text',
      textDecoration: 'none',
      textAlign: 'center',
      backgroundColor: 'background',
      fontFamily: 'body',
      fontWeight: 'bold',
      border: 'single',
      borderRadius: '10rem',
      minWidth: '10em',
      paddingY: 11,
      paddingX: 13,
      '&:hover': {
        color: 'contrast',
        border: 'primaryBg',
      },
    },
    buttonAlt: {
      display: 'inline-block',
      color: 'background',
      textDecoration: 'none',
      textAlign: 'center',
      backgroundColor: 'text',
      fontFamily: 'body',
      fontWeight: 'bold',
      border: 'single',
      borderRadius: '10rem',
      minWidth: '10em',
      paddingY: 11,
      paddingX: 13,
      '&:hover': {
        color: 'text',
        backgroundColor: 'muted',
        borderColor: 'text',
      },
    },
    filter: {
      display: 'inline-block',
      color: 'text',
      textDecoration: 'none',
      textAlign: 'center',
      backgroundColor: 'muted',
      fontFamily: 'body',
      fontWeight: 'bold',
      border: 'mutedBg',
      borderRadius: 0,
      minWidth: '8em',
      paddingY: 8,
      paddingX: 12,
      cursor: 'pointer',
      '&.active': {
        backgroundColor: 'primary',
        border: 'primaryBg',
      },
    },
    more: {
      display: 'inline-block',
      color: 'text',
      textDecoration: 'none',
      fontFamily: 'body',
      fontWeight: 'bold',
      border: 'single',
      width: '100%',
      maxWidth: '20em',
      textAlign: 'center',
      paddingY: 7,
      paddingX: 13,
      '&:hover': {
        color: 'contrast',
        border: 'primaryBg',
      },
    },
    anchor: {
      position: 'relative',
      fontWeight: 'bold',
      color: 'text',
      fontSize: '0.94rem',
      textDecoration: 'none',
      paddingLeft: '5.4rem',
      lineHeight: '4.2rem',
      '&::before': {
        content: '""',
        position: 'absolute',
        left: 0,
        width: '4rem',
        height: '4rem',
        border: 'single',
        borderRadius: '50%',
        backgroundImage: `url(${arrow})`,
        backgroundRepeat: 'no-repeat',
        backgroundPosition: '55% center',
        backgroundSize: '65%',
      },
      '&.flip::before': {
        transform: 'rotate(180deg)',
      },
      '&:hover': {
        color: 'contrast',
      },
      '&:hover::before': {
        backgroundImage: `url(${arrowGreen})`,
        borderColor: 'contrast',
      },
    },
    label: {
      color: 'text',
      fontWeight: 'bold',
      textDecoration: 'none',
      '&:hover': {
        color: 'contrast',
      },
    },
    primary: {
      color: 'primary',
      fontWeight: 'bold',
      '&:hover': {
        color: 'background',
      },
    },
    alt: {
      color: 'background',
      fontWeight: 'bold',
      '&:hover': {
        color: 'primary',
      },
    },
    plain: {
      color: 'text',
      textDecoration: 'none',
    },
  },
  forms: {
    input: {
      color: 'text',
      backgroundColor: 'muted',
      fontFamily: 'body',
      fontWeight: 'bold',
      width: 'auto',
      border: 'mutedBg',
      borderRadius: '10rem',
      minWidth: '10em',
      paddingY: 11,
      paddingX: 13,
      cursor: 'pointer',
    },
    complexFormInput: {
      // a variant for larger forms, emulated from switch frontend
      variant: 'theme.forms.input',
      ...formInput,
    },
    error: {
      variant: 'theme.forms.input',
      ...formInput,
      borderColor: 'error',
    },
    smallSelect: {
      appearance: 'none',
      color: 'text',
      fontFamily: 'body',
      fontWeight: 'bold',
      fontSize: 'inherit',
      lineHeight: 'inherit',
      border: 'mutedBg',
      borderRadius: 0,
      minWidth: '16em',
      paddingY: 8,
      paddingLeft: '1.5rem',
      paddingRight: '4rem',
      backgroundColor: 'muted',
      backgroundImage: `url(${arrowhead})`,
      backgroundRepeat: 'no-repeat',
      backgroundPosition: 'right 1.5rem center',
      backgroundSize: '1rem',
      cursor: 'pointer',
    },
    label: {
      marginBottom: '0.3rem',
      fontSize: '1.125rem',
    },
    textarea: {
      ...formInput,
      height: '8rem',
      padding: '1rem',
      margin: '0 0 2rem',
    },
    select: {
      '& + svg': {
        marginLeft: '-30px',
      },
    },
    radioGrey: {
      width: '30px',
      height: '30px',
      marginRight: '15px',
    },
    // wizard form variants (src/forms/wizard), from the EcoHub quote design
    // (Figma "EcoFlex Quoting Tool"): green outlines, 15px radii, Gottak
    // question titles, a black pill for Yes/No.
    range: {
      WebkitAppearance: 'none',
      appearance: 'none',
      display: 'block',
      width: '100%',
      height: '44px',
      margin: 0,
      padding: 0,
      backgroundColor: 'transparent',
      cursor: 'pointer',
      // The filled part of the track is a gradient the slider sets per value
      // (slider-field.tsx) through the --track custom property.
      '&::-webkit-slider-runnable-track': {
        height: '0.5rem',
        borderRadius: '0.25rem',
        background: 'var(--track)',
      },
      '&::-moz-range-track': {
        height: '0.5rem',
        borderRadius: '0.25rem',
        background: 'var(--track)',
      },
      '&::-webkit-slider-thumb': {
        WebkitAppearance: 'none',
        width: '1.5rem',
        height: '1.5rem',
        marginTop: '-0.5rem',
        borderRadius: '50%',
        backgroundColor: 'background',
        border: '2px solid',
        borderColor: 'contrast',
        boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
      },
      '&::-moz-range-thumb': {
        width: '1.5rem',
        height: '1.5rem',
        borderRadius: '50%',
        backgroundColor: 'background',
        border: '2px solid',
        borderColor: 'contrast',
        boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
      },
      '&:focus': { outline: 'none' },
      '&:focus-visible::-webkit-slider-thumb': {
        boxShadow: '0 0 0 0.25rem rgba(79,129,10,0.35)',
      },
      '&:focus-visible::-moz-range-thumb': {
        boxShadow: '0 0 0 0.25rem rgba(79,129,10,0.35)',
      },
    },
    // The slider's bordered panel: title and readout on one row, hint, track, bounds.
    sliderCard: {
      border: '1px solid',
      borderColor: 'contrast',
      borderRadius: '15px',
      padding: ['1rem', '1.5rem'],
      backgroundColor: 'background',
    },
    sliderTitle: {
      fontFamily: 'heading',
      fontWeight: 'bold',
      fontSize: ['1.25rem', '1.5rem'],
      lineHeight: 1.2,
      letterSpacing: '-0.02em',
      marginBottom: 0,
    },
    sliderReadout: {
      fontFamily: 'heading',
      fontWeight: 'bold',
      fontSize: ['1.75rem', '2.25rem'],
      lineHeight: 1.1,
      textAlign: 'right',
      whiteSpace: 'nowrap',
    },
    sliderBound: {
      fontFamily: 'heading',
      fontWeight: 'bold',
      fontSize: ['1rem', '1.25rem'],
      color: 'subtle',
    },
    fieldset: {
      border: 0,
      padding: 0,
      margin: 0,
      minWidth: 0,
    },
    // Question title for a group of inputs (Yes/No, cards, list).
    legend: {
      padding: 0,
      marginBottom: '0.5rem',
      fontFamily: 'heading',
      fontWeight: 'bold',
      fontSize: ['1.25rem', '1.5rem'],
      lineHeight: 1.2,
      letterSpacing: '-0.02em',
    },
    hint: {
      fontSize: '1rem',
      color: 'text',
      marginBottom: '0.75rem',
    },
    card: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: '0.75rem',
      border: '1px solid',
      borderColor: 'contrast',
      borderRadius: '10px',
      backgroundColor: 'background',
      padding: '1rem 1.25rem',
      marginBottom: '0.75rem',
      cursor: 'pointer',
      width: '100%',
      '&:hover': {
        borderColor: 'secondary',
        backgroundColor: 'darkMuted',
      },
      // The native radio inside carries keyboard focus; the whole card
      // shows it, so a keyboard user can see where they are.
      '&:focus-within': {
        outline: '3px solid',
        outlineColor: 'contrast',
        outlineOffset: '2px',
      },
    },
    cardSelected: {
      variant: 'forms.card',
      backgroundColor: 'accent',
      '&:hover': {
        borderColor: 'contrast',
        backgroundColor: 'accent',
      },
    },
    cardRadio: {
      flexShrink: 0,
      width: '1.25rem',
      height: '1.25rem',
      margin: '0.125rem 0 0',
      accentColor: '#4F810A',
      cursor: 'pointer',
    },
    cardTitle: {
      fontFamily: 'heading',
      fontWeight: 'bold',
      fontSize: ['1.125rem', '1.25rem'],
      lineHeight: 1.2,
    },
    cardSide: {
      fontWeight: 'bold',
      fontSize: '1rem',
      textAlign: 'right',
      whiteSpace: 'nowrap',
    },
    cardDescription: {
      fontSize: '0.875rem',
      color: 'subtle',
      marginTop: '0.25rem',
    },
    // Black pill holding the segmented options; the chosen one is acid green.
    segmentedGroup: {
      display: 'inline-flex',
      flexWrap: 'wrap',
      backgroundColor: 'text',
      border: '1px solid',
      borderColor: 'text',
      borderRadius: '10rem',
      padding: 0,
      margin: 0,
      listStyle: 'none',
      maxWidth: '100%',
      // The segmented inputs are visually hidden beside their labels, so the
      // label cannot use :focus-within; the group lights the label of the
      // input that has keyboard focus instead.
      '& input:focus-visible + label': {
        boxShadow: '0 0 0 3px #fff, 0 0 0 6px #4F810A',
      },
    },
    segmented: {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      flex: '1 1 auto',
      minHeight: '44px',
      padding: '0.5rem 1.25rem',
      borderRadius: '10rem',
      backgroundColor: 'transparent',
      color: 'background',
      fontWeight: 'bold',
      fontSize: '1rem',
      lineHeight: 1.2,
      cursor: 'pointer',
      width: 'auto',
      '&:hover': {
        textDecoration: 'underline',
      },
    },
    segmentedSelected: {
      variant: 'forms.segmented',
      backgroundColor: 'primary',
      color: 'text',
      '&:hover': {
        textDecoration: 'none',
      },
    },
    // Text-like inputs and selects: white, outlined, 8px radius (the site's
    // grey formInput is for the older custom forms).
    wizardInput: {
      display: 'block',
      width: '100%',
      minHeight: '3rem',
      padding: '0.625rem 1rem',
      fontFamily: 'body',
      fontSize: '1rem',
      fontWeight: 'body',
      lineHeight: 1.5,
      color: 'text',
      backgroundColor: 'background',
      border: '1px solid',
      borderColor: '#d4d7d7',
      borderRadius: '8px',
      marginBottom: 0,
      '&:focus': {
        outline: 'none',
        borderColor: 'contrast',
        boxShadow: '0 0 0 0.2rem rgba(79,129,10,0.35)',
      },
    },
    wizardInputError: {
      variant: 'forms.wizardInput',
      borderColor: 'error',
    },
    wizardSelect: {
      variant: 'forms.wizardInput',
      appearance: 'none',
      paddingRight: '2.75rem',
      cursor: 'pointer',
    },
    wizardSelectError: {
      variant: 'forms.wizardSelect',
      borderColor: 'error',
    },
    // Consent wording sits in a pale green panel.
    consentPanel: {
      border: '1px solid',
      borderColor: 'contrast',
      borderRadius: '10px',
      backgroundColor: 'accent',
      padding: '1rem 1.25rem',
    },
    // A featured text-like field (the field's Featured setting): label and
    // input in the same pale green panel.
    featuredPanel: {
      variant: 'forms.consentPanel',
      '& label': { fontSize: '1rem', fontWeight: 'bold' },
    },
    // "Why we ask" under the buttons.
    aside: {
      marginTop: '2.5rem',
      '& p': { fontSize: '1rem' },
    },
    asideTitle: {
      fontFamily: 'heading',
      fontWeight: 'bold',
      fontSize: ['1.25rem', '1.5rem'],
      lineHeight: 1.2,
      letterSpacing: '-0.02em',
      marginBottom: '0.5rem',
    },
    // An info step with an image: copy beside the image, stacked on a phone.
    infoWithImage: {
      flexDirection: ['column', null, 'row'],
      gap: ['1.5rem', null, '3rem'],
      alignItems: 'flex-start',
      marginBottom: '1.5rem',
    },
    infoImage: {
      borderRadius: '15px',
      overflow: 'hidden',
      '& img': { display: 'block', width: '100%', height: 'auto' },
    },
    // The success dialog (Success display `modal`): centred copy and Back to home.
    successModal: {
      textAlign: 'center',
      maxWidth: '40rem',
      paddingX: ['0', '1.5rem'],
    },
    successModalTitle: {
      fontFamily: 'heading',
      fontWeight: 'bold',
      fontSize: ['1.5rem', '1.75rem'],
      lineHeight: 1.15,
      letterSpacing: '-0.02em',
      marginBottom: '0.75rem',
    },
    // The not-eligible drop-out (result/ecohub-not-eligible.tsx): the same
    // dark panel as the quote page, then the buttons at the foot.
    dropoutPanel: {
      variant: 'forms.quoteHero',
      marginTop: '0.5rem',
    },
    dropoutTitle: {
      fontFamily: 'heading',
      fontWeight: 'bold',
      fontSize: ['1.75rem', '2.25rem'],
      lineHeight: 1.1,
      letterSpacing: '-0.02em',
      margin: '0 0 0.75rem',
    },
    dropoutFooter: {
      flexDirection: ['column', 'row'],
      justifyContent: 'center',
      alignItems: ['stretch', 'center'],
      gap: ['0.75rem', '1.5rem'],
      marginTop: '2rem',
      '& a': { textAlign: 'center' },
    },
    // The EcoHub quote page (result/ecohub-quote.tsx, Figma "Quote"): a dark
    // panel with the monthly figure, rate tiles, ruled summary rows and the
    // editor's bullets as cards.
    quoteHero: {
      backgroundColor: 'ink',
      color: 'background',
      borderRadius: '15px',
      padding: ['1.5rem', '2rem'],
      marginBottom: '1.5rem',
      // Links in the copy take the panel's white; the buttons beneath keep
      // their own colours (a bare `& a` outranks the button variant's text
      // colour and left "Learn more" white on white until hovered).
      '& p a, & li a': { color: 'inherit' },
    },
    quoteEyebrow: {
      color: 'primary',
      textTransform: 'uppercase',
      fontWeight: 'bold',
      fontSize: '0.875rem',
      letterSpacing: '0.04em',
      margin: '0 0 0.5rem',
    },
    quoteHeadline: {
      fontFamily: 'heading',
      fontWeight: 'bold',
      fontSize: ['2.75rem', '3.5rem'],
      lineHeight: 1,
      letterSpacing: '-0.02em',
      margin: '0 0 0.75rem',
    },
    quoteHeadlineUnit: {
      fontFamily: 'body',
      fontWeight: 'bold',
      fontSize: '1rem',
      letterSpacing: 0,
      marginLeft: '0.25rem',
    },
    quoteHeroCopy: {
      fontSize: '0.9375rem',
      marginBottom: '1.25rem',
      '& p': { margin: 0 },
    },
    quoteHeroActions: {
      flexDirection: ['column', 'row'],
      alignItems: ['stretch', 'center'],
      gap: '0.75rem',
    },
    quoteTiles: {
      display: 'grid',
      gridTemplateColumns: ['1fr 1fr', 'repeat(4, 1fr)'],
      gap: '0.75rem',
      marginBottom: '2rem',
    },
    quoteTile: {
      border: '1px solid',
      borderColor: 'contrast',
      borderRadius: '10px',
      padding: '1rem',
      backgroundColor: 'background',
    },
    quoteTileLabel: {
      fontWeight: 'bold',
      fontSize: '0.75rem',
      lineHeight: 1.3,
      textTransform: 'uppercase',
      margin: '0 0 0.75rem',
    },
    quoteTileValue: {
      fontFamily: 'heading',
      fontWeight: 'bold',
      fontSize: ['1.5rem', '1.75rem'],
      lineHeight: 1,
      margin: 0,
      whiteSpace: 'nowrap',
    },
    quoteTileUnit: {
      fontFamily: 'body',
      fontWeight: 'bold',
      fontSize: '0.75rem',
    },
    quoteSection: {
      marginBottom: '2rem',
    },
    quoteSectionTitle: {
      fontFamily: 'body',
      fontWeight: 'bold',
      fontSize: '1.125rem',
      lineHeight: 1.3,
      margin: '0 0 0.25rem',
      paddingBottom: '0.5rem',
      borderBottom: '1px solid',
      borderColor: 'text',
    },
    quoteRow: {
      justifyContent: 'space-between',
      alignItems: 'baseline',
      gap: '1rem',
      padding: '0.625rem 0',
      borderBottom: '1px solid',
      borderColor: 'muted',
      fontSize: '0.875rem',
      '& > span:last-of-type': { textAlign: 'right' },
    },
    quoteRowGroup: {
      variant: 'forms.quoteRow',
      fontWeight: 'bold',
      borderColor: 'text',
    },
    quoteCopy: {
      marginBottom: '1.5rem',
    },
    quoteCards: {
      listStyle: 'none',
      padding: '0.5rem 0 0',
      margin: 0,
      display: 'grid',
      gridTemplateColumns: ['1fr', 'repeat(3, 1fr)'],
      gap: '1rem',
    },
    quoteCard: {
      border: '1px solid',
      borderColor: 'contrast',
      borderRadius: '10px',
      padding: '1.25rem',
      fontSize: '0.875rem',
      lineHeight: 1.5,
      '&::before': {
        content: '""',
        display: 'block',
        width: '2rem',
        height: '2rem',
        marginBottom: '0.75rem',
        borderRadius: '6px',
        backgroundColor: 'accent',
      },
      '& p': { margin: 0 },
      '& strong': {
        display: 'block',
        fontFamily: 'heading',
        fontSize: '1rem',
        lineHeight: 1.2,
        marginBottom: '0.5rem',
      },
    },
  },
  buttons: {
    primary: {
      display: 'inline-block',
      color: 'text',
      textAlign: 'center',
      textDecoration: 'none',
      border: 'solid 1px',
      borderColor: 'primary',
      backgroundColor: 'primary',
      fontFamily: 'body',
      fontWeight: 'bold',
      borderRadius: '10rem',
      minWidth: '15em',
      padding: '21px',
      lineHeight: 1,
      fontSize: '16px',
      cursor: 'pointer',
      transition: 'background-color 0.2s linear, color 0.2s linear, border-color 0.2s linear',

      '&:hover': {
        color: 'background',
        backgroundColor: 'text',
        borderColor: 'primary',
      },
    },
    secondary: {
      color: 'text',
      backgroundColor: 'background',
      fontWeight: 'bold',
      border: 'single',
      borderRadius: '10rem',
      minWidth: '10em',
      paddingY: 11,
      paddingX: 13,
      cursor: 'pointer',
      '&:hover': {
        color: 'contrast',
        border: 'primaryBg',
      },
    },
    alt: {
      color: 'text',
      backgroundColor: 'primary',
      fontWeight: 'bold',
      border: 'single',
      borderRadius: '10rem',
      borderColor: 'contrast',
      minWidth: '10em',
      paddingY: 11,
      paddingX: 13,
      cursor: 'pointer',
      '&:hover': {
        color: 'background',
        backgroundColor: 'primary',
        borderColor: 'contrast',
      },
    },
    icon: {
      padding: 0,
      cursor: 'pointer',
      img: { width: '100%' },
    },
    square: {
      width: '5rem',
      height: 'auto',
      borderLeft: ['none', null, 'muted'],
      borderRadius: 0,
      cursor: 'pointer',
      img: { height: '1.5rem' },
    },
    rect: {
      color: 'text',
      backgroundColor: 'primary',
      fontFamily: 'body',
      fontWeight: 'bold',
      border: 'primaryBg',
      borderRadius: '0',
      minWidth: '14em',
      paddingY: 10,
      paddingX: 12,
      cursor: 'pointer',
      '&:hover': {
        color: 'background',
        backgroundColor: 'primary',
        borderColor: '#95C93E',
      },
    },
    rectSecondary: {
      color: 'text',
      backgroundColor: 'background',
      fontFamily: 'body',
      fontWeight: 'bold',
      border: 'single',
      borderRadius: '0',
      minWidth: '14em',
      paddingY: 10,
      paddingX: 12,
      cursor: 'pointer',
      '&:hover': {
        color: 'contrast',
        border: 'primaryBg',
      },
    },
    round: {
      padding: 6,
      width: '3.6rem',
      height: '3.6rem',
      cursor: 'pointer',
      backgroundColor: 'text',
      borderRadius: '50%',
      img: { width: '100%', filter: 'invert(1)' },
    },
    // wizard forms (src/forms/wizard): 44px pills, acid green with a green
    // outline for Next, white with a green outline for Previous.
    wizardPrimary: {
      variant: 'buttons.primary',
      minWidth: '9.5rem',
      minHeight: '44px',
      padding: '0.625rem 2.5rem',
      borderColor: 'contrast',
      '&:hover': {
        color: 'background',
        backgroundColor: 'contrast',
        borderColor: 'contrast',
      },
      '&:disabled': {
        cursor: 'default',
        opacity: 0.6,
      },
    },
    // On the quote page's dark panel: the acid-green link to join and a
    // white Next.
    wizardHeroPrimary: {
      variant: 'buttons.wizardPrimary',
      // A link, so it needs the centring a button gets for free.
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderColor: 'primary',
      textDecoration: 'none',
      '&:hover': {
        color: 'text',
        backgroundColor: 'background',
        borderColor: 'background',
        textDecoration: 'none',
      },
    },
    wizardHeroSecondary: {
      variant: 'buttons.wizardPrimary',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: 'text',
      backgroundColor: 'background',
      borderColor: 'background',
      '&:hover': {
        color: 'text',
        backgroundColor: 'primary',
        borderColor: 'primary',
      },
    },
    // wizardSecondary worn by a link (Back to home): an <a> has no button
    // box, so it needs the centring a button gets for free.
    wizardSecondaryLink: {
      variant: 'buttons.wizardSecondary',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      textDecoration: 'none',
      '&:hover': {
        color: 'background',
        backgroundColor: 'contrast',
        borderColor: 'contrast',
        textDecoration: 'none',
      },
    },
    // Same box as wizardPrimary (font, line height, border width, padding),
    // so Previous and Next sit at one height; only the colours differ.
    wizardSecondary: {
      variant: 'buttons.wizardPrimary',
      color: 'contrast',
      backgroundColor: 'background',
      borderColor: 'contrast',
      '&:hover': {
        color: 'background',
        backgroundColor: 'contrast',
        borderColor: 'contrast',
      },
    },
  },
}
